<script lang="ts">
	import { beforeUpdate, onDestroy, onMount } from 'svelte';
	import { format } from '../../../services/i18n';
	import { scenes } from 'src/stores/lists/scenes';
	import { _calculate_duration } from 'src/helpers/utilities';
	import {
		resetSceneMainFilter,
		roomsScenesFilter,
		sceneMainFilter,
		getSelectedSceneFilterCount,
	} from 'src/stores/ui/filters';
	import { defaultSelectedRoom } from 'src/helpers/constants';
	import { ComponentType } from '@Shelly/shelly-design-system/helpers/enums';
	import { user_data } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	import { edit } from 'src/stores/ui/edit_lists';
	import AddEditInterval from 'src/components/Scenes/SceneWizard/Process/ActiveTime/AddEditInterval.svelte';
	import { calculateWhenEnd, load_image_from } from 'src/helpers/interface_helpers';
	import { requestReorderDevice, onStartDrag, onEndDrag } from '../list-helpers';
	import { normalizeSceneDoStatements } from 'src/components/Flows/converters/flow-island-utils';

	import ScenesFilterModal from './ScenesFilterModal.svelte';
	import AddEditButtons from '../../AddEditButtons.svelte';

	import {
		CardGrid,
		Modal,
		Toolbar,
		Breadcrumbs,
		IconButton,
		toast,
		Input,
	} from '@Shelly/shelly-design-system';
	import SceneWizard from '../../Scenes/SceneWizard/SceneWizard.svelte';
	import EmptyList from '../EmptyList.svelte';
	import ListEmptyStateSurface from '../ListEmptyStateSurface.svelte';
	import { updateCopyLabels } from '../../Scenes/helpers/sceneUtils';
	import RoomToolbar from '../Rooms/RoomToolbar.svelte';
	import RoomBreadCrumbs from '../Rooms/RoomBreadCrumbs.svelte';

	import type { Device } from '../../../types/Device';
	import type { ISceneObj } from '../../../types/SceneType';
	import { scene_object_prototype } from '../../Scenes/SceneWizard/scene_object';
	import { capFirstChar, _deepClone } from '../../../helpers/utilities';
	import { get } from 'svelte/store';
	import { LabelTypes } from '@Shelly/shelly-design-system/Label/label-helpers';
	import { hasMissingSceneData } from 'src/components/Scenes/helpers/has_missing_scene_data';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { isExperimentalEnabled } from 'src/helpers/user_settings_helpers';
	import RoomToolbarWrapper from '../Rooms/RoomToolbarWrapper.svelte';
	import ScenesToolbar from './ScenesToolbar.svelte';
	import ScenesListContent from './ScenesListContent.svelte';
	import ListSkeletonGrid from '../ListSkeletonGrid.svelte';
	import { forceListSkeleton } from 'src/stores/features/dev/force-list-skeleton';
	import {
		checkRadioFavorites,
		hasRadioFavorites,
	} from 'src/components/Scenes/helpers/radio_favorites_cache';
	import { actionInterpretator } from 'src/components/Scenes/helpers/scene_actions_to_ui';
	import {
		getDelayedSceneAction,
		getSceneActionPurpose,
	} from 'src/components/Scenes/helpers/get_delayed_scene_action';
	import { currentUserReceivesSceneNotifications } from 'src/components/Scenes/helpers/notification_recipients_helpers';
	import { createDeviceID, isWallDisplay } from 'src/helpers/interface_helpers';
	import { CreateScriptScene, ScriptSceneEditor } from '@Shelly/scenes-script';
	import ShellyScriptEditor from 'src/components/DevicesSettings/Scripts/Editor/ShellyScriptEditor';
	import SceneDeleteModal from 'src/components/Scenes/SceneDeleteModal.svelte';
	import { aiSceneChatStore } from 'src/stores/features/ai-scene-chat';
	import { clearScenesItemNewFlag } from 'src/helpers/add-entity-helpers';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';
	import {
		getSharedHomeEmptyListCopy,
		type SharedHomeManagedContext,
	} from 'src/helpers/houses/shared_home_messaging_helpers';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';

	export let list;
	export let type = 'scenes';
	export let id; // the id from route, whatever it is

	let scene_id;
	let scene_name = '';
	let scene_object: ISceneObj = _deepClone(scene_object_prototype) as ISceneObj;
	let isScriptScene = false;

	const requestType = 'scene';
	const flipDurationMs = 0;

	let showAddEditSceneModal = false;
	let showSharedManagedModal = false;
	let sharedManagedContext: SharedHomeManagedContext = 'add_scene';
	let showDeleteSceneModal = false;
	let openFilterModal = false;
	let dragDisabled = true;
	let currentRoute = '';
	let currentRoomFilter = defaultSelectedRoom;
	let sceneLabels = {};
	let isCheckingRadioFavorites = false;
	let showAddScriptSceneModal = false;
	let scriptSceneForEdit: ISceneObj = null;
	let showScriptEditorModal = false;

	$: isEmptyDeviceList =
		Object.values($devices).length === 0 ||
		Object.values($devices).filter((d: Device) => !d.shared).length === 0;
	$: isRoomFilter =
		type !== 'scenes'
			? true
			: $roomsScenesFilter.rooms.find((filter) => filter.value).value !==
				defaultSelectedRoom.value;

	$: notification =
		getSelectedSceneFilterCount($roomsScenesFilter) > 0
			? ({
					title: getSelectedSceneFilterCount($roomsScenesFilter),
					type: ComponentType.DANGER,
				} as const)
			: null;

	$: items = $edit && currentRoute === type && !isRoomFilterChanged() ? items : [...list];
	$: dndSettings = {
		items,
		flipDurationMs: flipDurationMs,
		dragDisabled,
	};

	// Reactive statement to update copy labels when items change. Also depends on
	// `labelDefinitions` so labels are rebuilt with the localized text once the
	// i18n messages finish loading (otherwise labels created during the initial
	// render stay frozen with the English `default` values after a refresh).
	$: if (items && items.length > 0 && labelDefinitions) {
		const copyLabels = updateSceneCopyLabels();
		const finalLabels = updateMissingSceneData(copyLabels);
		sceneLabels = updateUserRecipientLabels(finalLabels);
	}

	onMount(() => {
		currentRoute = type;
	});

	onDestroy(() => {
		edit.set(false);
		resetSceneMainFilter();
		clearScenesItemNewFlag(id);
	});

	beforeUpdate(() => {
		if (!$edit) handleEndDrag();

		if (currentRoute !== type) {
			currentRoute = type;
		}
	});

	const isRoomFilterChanged = () => {
		if (currentRoomFilter.value !== $roomsScenesFilter.rooms.find((filter) => filter.value).value) {
			currentRoomFilter = {
				...$roomsScenesFilter.rooms.find((filter) => filter.value),
			};
			return true;
		}

		return false;
	};

	const onCardClick = (item) => {
		if ($edit) return;

		setSidePanelState({ type: 'scene', id: item._id, open: true });
	};

	const handle_add_btn = () => {
		if (isActiveHomeShared($homes.list)) {
			sharedManagedContext = 'add_scene';
			showSharedManagedModal = true;
			return;
		}
		edit.set(false);
		scene_id = '';
		scene_object = _deepClone(scene_object_prototype) as ISceneObj;
		if (type === 'room') {
			scene_object._meta.room = +id;
		}
		showAddEditSceneModal = true;
	};

	const onCardEdit = async (event: MouseEvent | TouchEvent, item) => {
		event.stopPropagation();
		if (item?.infinno) {
			scriptSceneForEdit = _deepClone(item) as ISceneObj;
			showScriptEditorModal = true;
		} else {
			scene_id = item._id;
			showAddEditSceneModal = true;
			console.log('EDIT SCENE ID ', scene_id);
			scene_object = _deepClone($scenes[scene_id]) as ISceneObj;
			scene_name = item._meta.name;
		}
	};

	const onCardDelete = (event: MouseEvent | TouchEvent, item) => {
		event.stopPropagation();
		scene_id = item._id;
		scene_name = item._meta.name;
		isScriptScene = !!item?.infinno;
		showDeleteSceneModal = true;
	};

	// DND Functions
	const handleStartDrag = () => {
		dragDisabled = false;
	};

	const handleEndDrag = () => {
		dragDisabled = true;
	};

	const handleReorder = (e) => {
		items = e.detail.items;
	};

	const handleReorderEnd = async (e) => {
		let editedList = {};

		console.log(':event items ', e.detail.items);
		e.detail.items.map((v, i) => {
			delete v?.isDndShadowItem;
			v.id = String(v.id);
			v._id = String(v._id);
			v._name = String(v._id); // PHP-API legacy identifier. PHP-API bulk_update relies on it on REORDER

			editedList[v._id] = {
				...v,
				_meta: {
					...v._meta,
					position: i,
				},
			};
		});

		if (Object.keys(editedList).length === 0) return;
		console.log('edited list', editedList);

		items = [...e.detail.items];
		handleEndDrag();

		await requestReorderDevice(requestType, editedList);
		console.log('REORDERED ITEMS', items);
	};

	/**
	 * Numeric ids in this map double as the label kind so we can reconcile labels in
	 * {@link updateMissingSceneData} without losing other kinds (e.g. the duplicate-scene
	 * label). Keep ids unique per kind.
	 *
	 * Reactive on `$format` so labels re-translate when locale messages finish loading after
	 * a page refresh.
	 */
	$: labelDefinitions = {
		hasMissingDevice: {
			icon: 'fas fa-warning',
			id: 0,
			value: $format('missing_device_label', { default: 'Missing Device' }),
			type: LabelTypes.WARNING,
		},
		hasMissingWeatherCondition: {
			icon: 'fas fa-warning',
			id: 1,
			value: $format('invalid_weather_condition_label', {
				default: 'Invalid weather condition',
			}),
			type: LabelTypes.WARNING,
		},
		hasNoFavoriteRadio: {
			icon: 'fas fa-warning',
			id: 3,
			value: $format('no_favorite_radio_stations_title', {
				default: 'No favorite radio stations',
			}),
			type: LabelTypes.WARNING,
		},
		isCopy: (originalName: string) => ({
			icon: 'fas fa-copy',
			id: 2,
			value: $format('duplicated', {
				default: 'Duplicated',
			}),
			originalScene: originalName, // Store original scene name for drawer use
			type: LabelTypes.PRIMARY_10,
		}),
		// FE-1682: marks scenes where any push/mail notification action targets the
		// currently logged-in account.
		userReceivesNotifications: {
			icon: 'fas fa-bell',
			id: 4,
			value: $format('scene_label_user_receives_notifications', {
				default: 'You get notified',
			}),
			type: LabelTypes.PRIMARY_10,
		},
	};

	function updateSceneCopyLabels() {
		const createCopyLabel = (originalName: string) => labelDefinitions.isCopy(originalName);
		return updateCopyLabels(items, sceneLabels, createCopyLabel, 2);
	}

	function updateMissingSceneData(currentLabels = sceneLabels) {
		// Create a new labels object to avoid mutation issues
		const newSceneLabels = { ...currentLabels };

		//This function will update the sceneLabels object with the missing data for each scene. It will be called on every update of the scenes list.
		for (const scene of items) {
			const isMissingData = hasMissingSceneData(scene)?.missingData;
			// Initialize scene labels if undefined
			if (typeof newSceneLabels[scene._id] === 'undefined') {
				newSceneLabels[scene._id] = [];
			}

			if (isMissingData) {
				// Add missing data labels, but preserve existing copy labels.
				// Always drop a previous entry with the same id before inserting
				// the new one so the label picks up the latest translation when
				// the locale messages finish loading.
				isMissingData.forEach((missingData) => {
					const labelDef = labelDefinitions[missingData];
					if (labelDef && typeof labelDef === 'object') {
						newSceneLabels[scene._id] = [
							...newSceneLabels[scene._id].filter((label) => label.id !== labelDef.id),
							labelDef,
						];
					}
				});
			} else {
				// Remove only missing data labels (id 0, 1, and 3), keep copy labels (id 2)
				// and the FE-1682 "user receives notifications" label (id 4).
				newSceneLabels[scene._id] = newSceneLabels[scene._id].filter(
					(label) => label.id === 2 || label.id === 4,
				);

				// Only delete if truly empty (no copy labels either)
				if (newSceneLabels[scene._id].length === 0) {
					delete newSceneLabels[scene._id];
				}
			}
		}

		return newSceneLabels;
	}

	/**
	 * FE-1682: applies/removes the "you get notified" label based on the scene's
	 * notification recipients. Runs alongside the existing label refreshers so
	 * the badge stays in sync as recipients are added/removed.
	 */
	function updateUserRecipientLabels(currentLabels: Record<string, any[]> = sceneLabels) {
		const newSceneLabels: Record<string, any[]> = { ...currentLabels };
		const labelDef = labelDefinitions.userReceivesNotifications;

		for (const scene of items) {
			const receives = currentUserReceivesSceneNotifications(scene);
			const existing = newSceneLabels[scene._id] ?? [];
			const withoutBadge = existing.filter((label) => label.id !== labelDef.id);

			if (receives) {
				newSceneLabels[scene._id] = [...withoutBadge, labelDef];
			} else if (withoutBadge.length > 0) {
				newSceneLabels[scene._id] = withoutBadge;
			} else {
				delete newSceneLabels[scene._id];
			}
		}

		return newSceneLabels;
	}

	const getTranslatedAction = (element) => {
		const delayedAction = getDelayedSceneAction(element);
		const msg_type = element.msg_type;
		const purpose = getSceneActionPurpose(element, delayedAction);

		const actionElement = delayedAction !== undefined ? { ...delayedAction } : element;

		return actionInterpretator(purpose, actionElement, msg_type);
	};

	const shouldCheckRadioFavorites = (deviceId, device, translated) => {
		if (!device || !translated || !('device' in translated)) {
			return false;
		}
		const isWallDisplayMediaPlayer =
			device?.category === 'media_player' && isWallDisplay(device?.type);
		const hasRadioPlayFavouriteAction = translated.actions?.some(
			(action) => action.name === 'Radio_PlayFavourite',
		);
		const cached = hasRadioFavorites(deviceId);
		const isNotCached = cached === undefined;

		return isWallDisplayMediaPlayer && hasRadioPlayFavouriteAction && isNotCached;
	};

	async function checkRadioFavoritesForScenes() {
		if (isCheckingRadioFavorites) {
			return;
		}
		isCheckingRadioFavorites = true;

		try {
			const _devices = get(devices);
			const devicesToCheck = new Set<string>();

			for (const scene of items) {
				const thens = normalizeSceneDoStatements(scene?.do);
				for (const element of thens) {
					let translated;
					try {
						translated = getTranslatedAction(element);
					} catch (error) {
						console.error('Failed to inspect scene action for radio favorites', error);
						continue;
					}
					if (!translated || !('device' in translated) || !translated.device) {
						continue;
					}

					const device_id = createDeviceID(
						translated.device.id,
						translated.device.channel,
						_devices[translated.device.id]?.type,
					);
					const device = _devices[device_id];

					if (shouldCheckRadioFavorites(device_id, device, translated)) {
						devicesToCheck.add(device_id);
					}
				}
			}

			const checkPromises = Array.from(devicesToCheck).map((deviceId) => {
				const device = _devices[deviceId];
				if (device) {
					return checkRadioFavorites(device);
				}
				return Promise.resolve(false);
			});

			await Promise.all(checkPromises);
			const copyLabels = updateSceneCopyLabels();
			const finalLabels = updateMissingSceneData(copyLabels);
			sceneLabels = updateUserRecipientLabels(finalLabels);
		} finally {
			isCheckingRadioFavorites = false;
		}
	}

	function onScriptEditorClose() {
		showScriptEditorModal = false;
		scriptSceneForEdit = null;
	}

	$: {
		if ($devices || $scenes) {
			checkRadioFavoritesForScenes(); //update the missingDataLabels object with the missing data on every update of the scenes list or devices list
		}
	}

	function onSuccessfulSceneCreation(e: CustomEvent) {
		const newSceneId = e.detail;
		setTimeout(() => {
			const sceneCard = document.getElementById(`scene-card-${newSceneId}`);
			if (sceneCard) {
				sceneCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
				onCardClick($scenes[newSceneId]);
			}

			toast.open({
				type: 'success',
				title: 'Scene created successfully',
			});
		}, 300);
	}

	const lazyShowChatModal = () => {
		aiSceneChatStore.toggleModal();
	};
</script>

{#if $showNewLayout && type === 'room'}
	<RoomToolbarWrapper
		showBulkActions={false}
		{id}
		{type}
		{list}
		{currentRoute}
		itemsLength={items.length}
	/>
{:else if $showNewLayout}
	<ScenesToolbar
		{type}
		{id}
		{lazyShowChatModal}
		disableReorder={items.length <= 1}
		bind:showAddScriptSceneModal
	/>
{:else}
	<Toolbar class="mb-m" isSticky>
		<svelte:fragment slot="breadcrumbs">
			{#if type === 'room'}
				<RoomBreadCrumbs {id} />
			{:else}
				<Breadcrumbs
					links={[{ label: capFirstChar($format('tab_scenes')) }]}
					aria-label={$format('label_scenes', { default: 'Scenes' })}
				/>
			{/if}
		</svelte:fragment>
		<svelte:fragment slot="leftSide">
			{#if type === 'room'}
				<RoomToolbar />
			{/if}

			{#if type === 'scenes'}
				<div class="d-flex align-items-center justify-content-center">
					<Input
						class="width-md-m mr-sm-s text-capitalize-placeholder"
						bind:value={$sceneMainFilter}
						type="text"
						label={capFirstChar($format('search', { default: 'Search' }))}
						icon="fas fa-search"
						id="scenes-toolbar-search-input"
						aria-labelledby="scenes-toolbar-search-input"
					/>
					<IconButton
						icon="fa-solid fa-filter"
						on:click={() => {
							openFilterModal = true;
						}}
						aria-label={$format('aria_open_filter', { default: 'Open filter' })}
						{notification}
					/>
				</div>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="rightSide">
			{#if isExperimentalEnabled('inf2') && !$edit}
				<IconButton
					aria-label="Add Script Scene"
					class="align-self-center"
					icon="fa-brands fa-js"
					disabled={false}
					on:click={() => {
						showAddScriptSceneModal = !showAddScriptSceneModal;
					}}
				/>
			{/if}

			<AddEditButtons
				disableEditButton={items.length <= 0}
				disableAddButton={isEmptyDeviceList}
				interceptEditClick={isActiveHomeShared($homes.list)}
				on:add_click={() => handle_add_btn()}
				on:edit_click={() => {
					sharedManagedContext = 'edit_entity';
					showSharedManagedModal = true;
				}}
			/>
		</svelte:fragment>
	</Toolbar>
{/if}

<!-- {#if isEmptyDeviceList && !Object.keys($scenes).some((scene) => !Object.keys($temp_schemes)
				.concat(Object.keys($alarms))
				.includes(scene))}
	<EmptyDeviceList />
	{/if} -->
{#if items.length <= 0 && !!$sceneMainFilter}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<EmptyList
			icon="fal fa-magnifying-glass"
			title={$format('no_such_scene_found', { default: 'No such scenes found.' })}
			description={$format('check_the_spelling_or_try_new_search', {
				default: 'Check the spelling or try a new search.',
			})}
		/>
	</ListEmptyStateSurface>
{:else if items.length <= 0 && isActiveHomeShared($homes.list)}
	{@const sharedEmptyCopy = getSharedHomeEmptyListCopy('scenes')}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<EmptyList
			icon="fal fa-film"
			title={$format(sharedEmptyCopy.titleKey, { default: sharedEmptyCopy.titleDefault })}
			description={$format(sharedEmptyCopy.descrKey, {
				default: sharedEmptyCopy.descrDefault,
			})}
		/>
	</ListEmptyStateSurface>
{:else if items.length <= 0}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<EmptyList
			icon="fal fa-film"
			title={$format('no_scenes_title', {
				default: 'There are no scenes yet',
			})}
			description={$format('no_scenes_descr', {
				default: 'Try creating a scene for automating actions of your devices.',
			})}
			btnText={$format('button_add_scene', { default: 'Add scene' })}
			onClick={() => handle_add_btn()}
		/>
	</ListEmptyStateSurface>
{:else if $forceListSkeleton}
	<ListSkeletonGrid class={$edit && $showNewLayout ? 'mt-s' : ''} variant="list-menu" />
{:else}
	<CardGrid
		class={$edit && $showNewLayout ? 'mt-s' : ''}
		editMode={$edit}
		xl={350}
		lg={290}
		sm={270}
		{dndSettings}
		on:consider={handleReorder}
		on:finalize={handleReorderEnd}
	>
		{#each items as item, _ (item)}
			<div
				class:dnd-placeholder={item.isDndShadowItem}
				tabindex="0"
				role="button"
				aria-label={$format('button_drag_scene_to_reorder', {
					default: 'Drag scene to reorder',
				})}
			>
				<ScenesListContent
					{item}
					{load_image_from}
					{onCardDelete}
					{onCardEdit}
					{sceneLabels}
					{onCardClick}
					on:handle-drag-start={(e) => onStartDrag(e, $edit, handleStartDrag, true)}
					on:handle-drag-end={(e) => onEndDrag(e, $edit, handleEndDrag, true)}
					on:mousedown={(e) => onStartDrag(e, $edit, handleStartDrag)}
					on:touchstart={(e) => onStartDrag(e, $edit, handleStartDrag)}
					on:mouseup={(e) => onEndDrag(e, $edit, handleEndDrag)}
					on:touchend={(e) => onEndDrag(e, $edit, handleEndDrag)}
				/>
			</div>
		{/each}
	</CardGrid>
{/if}

{#if showAddEditSceneModal}
	<SceneWizard bind:id={scene_id} bind:showAddEditModal={showAddEditSceneModal} {scene_object} />
{/if}

<SceneDeleteModal bind:showDeleteSceneModal bind:items {scene_id} {scene_name} {isScriptScene} />

<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context={sharedManagedContext} />

<!-- Filter Modal -->
{#if openFilterModal}
	<ScenesFilterModal bind:openFilterModal />
{/if}

{#if scriptSceneForEdit}
	<Modal
		type="fullScreen"
		backdropClose={false}
		showModal={showScriptEditorModal}
		on:close={() => onScriptEditorClose()}
		title="Script scene editor"
	>
		<ScriptSceneEditor
			slot="content"
			user_data={$user_data}
			scene={scriptSceneForEdit}
			activeTimeCalculationHelpers={{
				_calculate_duration,
				calculateWhenEnd,
			}}
			activeTimeComponent={AddEditInterval}
			ScriptEditorClass={ShellyScriptEditor}
		/>
	</Modal>
{/if}

{#if showAddScriptSceneModal}
	<CreateScriptScene
		user_data={$user_data}
		activeTimeCalculationHelpers={{
			_calculate_duration,
			calculateWhenEnd,
		}}
		activeTimeComponent={AddEditInterval}
		ScriptEditorClass={ShellyScriptEditor}
		{showAddScriptSceneModal}
		on:close={() => (showAddScriptSceneModal = false)}
		on:sceneCreated={(e) => onSuccessfulSceneCreation(e)}
	></CreateScriptScene>
{/if}
